// AUTO-GENERATED — do not edit. Run `pnpm build` in packages/domain-data.
// Source: business/ishvaram/data/domain/astrology/doshas.yaml

export interface DoshaRemedy {
  type: string;
  reason: string;
  priority?: number;
}

export interface Dosha {
  id: string;
  name_en: string;
  name_hi: string;
  description: string;
  /** Primary planet for single-planet doshas (absent for axis/conjunction doshas like kaal_sarp/pitra) */
  planet?: string;
  /** Houses that trigger this dosha (absent for axis-type doshas) */
  trigger_houses?: number[];
  /** Severity weight per house (absent for axis-type doshas) */
  severity_by_house?: Record<string, number>;
  remedies: DoshaRemedy[];
  affects: string[];
  /** Optional extra fields (cancellation_conditions, additional_conditions, sub_types, etc.) */
  [key: string]: unknown;
}

export const doshas: readonly Dosha[] = [
  {
    id: "mangal_dosh",
    name_en: "Mangal Dosha (Manglik)",
    name_hi: "मांगलिक दोष",
    description:
      "Mars in 1st, 2nd, 4th, 7th, 8th, or 12th house causes delays/conflicts in marriage",
    planet: "mars",
    trigger_houses: [1, 2, 4, 7, 8, 12],
    severity_by_house: {
      "1": 0.5,
      "2": 0.6,
      "4": 0.7,
      "7": 1,
      "8": 0.9,
      "12": 0.8,
    },
    cancellation_conditions: [
      "Mars in own sign (Aries/Scorpio) in trigger house",
      "Mars aspected by benefic Jupiter",
      "Mars in Capricorn (exalted)",
      "Both partners are Manglik",
      "Mars conjunct or aspected by Moon",
    ],
    remedies: [
      {
        type: "karungali_wood",
        reason: "Karungali (ebony) absorbs Mars's excess energy and channels it protectively",
      },
      {
        type: "rudraksha_bracelet",
        reason: "Rudraksha pacifies Mars aggression and brings inner calm",
      },
      {
        type: "hanuman_prayer",
        reason: "Recite Hanuman Chalisa on Tuesdays",
      },
    ],
    affects: ["marriage", "relationships", "temperament"],
  },
  {
    id: "shani_dosh",
    name_en: "Shani Dosha",
    name_hi: "शनि दोष",
    description: "Saturn afflicting key houses causes delays, hardship, and karmic lessons",
    planet: "saturn",
    trigger_houses: [1, 4, 7, 8, 10],
    severity_by_house: {
      "1": 0.7,
      "4": 0.8,
      "7": 0.9,
      "8": 1,
      "10": 0.7,
    },
    additional_conditions: [
      {
        name: "sade_sati",
        description: "Saturn transiting 12th, 1st, and 2nd from Moon sign (7.5 year period)",
        severity: 0.8,
      },
      {
        name: "dhaiya",
        description: "Saturn transiting 4th or 8th from Moon sign (2.5 year period)",
        severity: 0.5,
      },
      {
        name: "shani_mahadasha",
        description: "Running Saturn mahadasha (19 years)",
        severity: 0.6,
      },
    ],
    remedies: [
      {
        type: "karungali_wood",
        reason: "Karungali (ebony wood) is the primary remedy for Shani — Saturn's sacred wood",
        priority: 1,
      },
      {
        type: "shani_prayer",
        reason: "Recite Shani Stotram on Saturdays, donate black items",
      },
      {
        type: "iron_ring",
        reason: "Wear iron ring on middle finger (Saturn's finger)",
      },
    ],
    affects: ["career", "health", "longevity", "obstacles", "karma"],
  },
  {
    id: "rahu_dosh",
    name_en: "Rahu Dosha",
    name_hi: "राहु दोष",
    description: "Rahu in certain houses causes confusion, obsession, and unconventional behavior",
    planet: "rahu",
    trigger_houses: [1, 5, 7, 8, 12],
    severity_by_house: {
      "1": 0.7,
      "5": 0.8,
      "7": 0.8,
      "8": 0.9,
      "12": 0.7,
    },
    remedies: [
      {
        type: "rudraksha_mala",
        reason: "Rudraksha protects from Rahu's illusionary energy and grounds the mind",
        priority: 1,
      },
      {
        type: "hessonite_stone",
        reason: "Hessonite (Gomed) gemstone pacifies Rahu",
      },
      {
        type: "rahu_prayer",
        reason: "Recite Rahu Beej Mantra, donate on Saturdays",
      },
    ],
    affects: ["mental_peace", "clarity", "foreign_matters", "obsessions"],
  },
  {
    id: "ketu_dosh",
    name_en: "Ketu Dosha",
    name_hi: "केतु दोष",
    description:
      "Ketu in certain houses causes detachment, spiritual crisis, and unexpected events",
    planet: "ketu",
    trigger_houses: [1, 4, 5, 7, 9],
    severity_by_house: {
      "1": 0.6,
      "4": 0.7,
      "5": 0.8,
      "7": 0.8,
      "9": 0.5,
    },
    remedies: [
      {
        type: "rudraksha_bracelet",
        reason: "Rudraksha balances Ketu's intense spiritual energy",
        priority: 1,
      },
      {
        type: "cats_eye_stone",
        reason: "Cat's Eye (Lehsunia) gemstone pacifies Ketu",
      },
      {
        type: "ganesha_worship",
        reason: "Worship Lord Ganesha to remove Ketu obstacles",
      },
    ],
    affects: ["spirituality", "detachment", "sudden_events", "moksha"],
  },
  {
    id: "kaal_sarp_dosh",
    name_en: "Kaal Sarp Dosha",
    name_hi: "काल सर्प दोष",
    description: "All 7 planets hemmed between Rahu and Ketu axis — intense karmic pattern",
    detection_type: "axis",
    severity: 0.9,
    sub_types: [
      {
        name: "anant",
        rahu_house: 1,
        ketu_house: 7,
      },
      {
        name: "kulik",
        rahu_house: 2,
        ketu_house: 8,
      },
      {
        name: "vasuki",
        rahu_house: 3,
        ketu_house: 9,
      },
      {
        name: "shankhpal",
        rahu_house: 4,
        ketu_house: 10,
      },
      {
        name: "padma",
        rahu_house: 5,
        ketu_house: 11,
      },
      {
        name: "mahapadma",
        rahu_house: 6,
        ketu_house: 12,
      },
      {
        name: "takshak",
        rahu_house: 7,
        ketu_house: 1,
      },
      {
        name: "karkotak",
        rahu_house: 8,
        ketu_house: 2,
      },
      {
        name: "shankhchoor",
        rahu_house: 9,
        ketu_house: 3,
      },
      {
        name: "ghatak",
        rahu_house: 10,
        ketu_house: 4,
      },
      {
        name: "vishdhar",
        rahu_house: 11,
        ketu_house: 5,
      },
      {
        name: "sheshnag",
        rahu_house: 12,
        ketu_house: 6,
      },
    ],
    remedies: [
      {
        type: "rudraksha_mala",
        reason:
          "Rudraksha mala is the primary remedy for Kaal Sarp — worn during Nag Panchami pooja",
        priority: 1,
      },
      {
        type: "karungali_wood",
        reason: "Karungali mala absorbs serpent (Rahu-Ketu) energy",
      },
      {
        type: "nag_panchami_pooja",
        reason: "Perform Nag Panchami pooja and donate at Nag temple",
      },
      {
        type: "shivling_abhishek",
        reason: "Offer milk on Shivling every Monday",
      },
    ],
    affects: ["overall_life", "karma", "obstacles", "relationships", "career"],
  },
  {
    id: "pitra_dosh",
    name_en: "Pitra Dosha",
    name_hi: "पितृ दोष",
    description:
      "Sun conjunct/aspected by Rahu/Ketu, or Sun in 9th with malefics — ancestral karma",
    detection_type: "conjunction",
    conditions: [
      "Sun conjunct Rahu in any house",
      "Sun conjunct Ketu in any house",
      "Sun conjunct Saturn in any house",
      "Sun in 9th house with Rahu or Ketu",
      "Rahu, Ketu, Saturn, or Mars in the 9th house",
      "9th lord afflicted by Rahu, Ketu, Saturn, or Mars",
    ],
    severity: 0.7,
    remedies: [
      {
        type: "rudraksha_bracelet",
        reason: "Rudraksha connects with ancestral blessings and Lord Shiva",
      },
      {
        type: "pitra_tarpan",
        reason: "Perform tarpan (offerings) during Pitru Paksha",
      },
      {
        type: "ganesha_idol",
        reason: "Keep energized Ganesha idol for removing ancestral obstacles",
      },
    ],
    affects: ["family", "ancestors", "father", "children", "fortune"],
  },
] as const;

export const doshaMap: Readonly<Record<string, Dosha>> = Object.fromEntries(
  doshas.map((d) => [d.id, d])
);
